import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { DIAS_CURTOS, DIAS_LONGOS, isoLocalDate, somaDias } from "@/components/Agenda/agenda-utils";
import { hhmmParaMinutos } from "@/components/Agenda/grade-utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, CalendarOff, Trash2 } from "lucide-react";

type Periodo = "dia" | "manha" | "tarde" | "horario";
type Escopo = "hoje" | "amanha" | "semana" | "datas" | "recorrente";

/** Motivos mais comuns — um clique em vez de digitar. */
const MOTIVOS_RAPIDOS = ["Férias", "Folga", "Treinamento", "Médico", "Viagem", "Reunião externa"];

export type BloqueioEditavel = {
  id: number;
  userId: number;
  startAt: Date | string;
  endAt: Date | string;
  reason: string | null;
  allDay: boolean;
};

export type BlockDefaults = {
  userId?: number;
  startAt?: Date;
  endAt?: Date;
};

/**
 * Bloquear (ou editar) um período da agenda de alguém.
 *
 * O desenho parte de como a pessoa fala: "estou fora hoje à tarde", "férias
 * na semana que vem". Por isso a escolha dos dias vem em atalhos (hoje,
 * amanhã, esta semana) e a parte do dia em cartões — não em dois campos de
 * data e dois de hora. "Escolher datas" continua ali para o resto.
 *
 * O resumo no rodapé é a frase que a pessoa confirmaria em voz alta: é ele
 * que pega o erro de ter bloqueado a semana inteira sem querer.
 */
export default function BlockDialog({
  open,
  onClose,
  bloqueio,
  defaults,
}: {
  open: boolean;
  onClose: () => void;
  /** Editando este bloqueio; ausente = criando. */
  bloqueio?: BloqueioEditavel | null;
  defaults?: BlockDefaults;
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const utils = trpc.useUtils();
  const meuId = (user as any)?.id as number | undefined;
  const podeGerenciarOutros = can("agenda:availability_manage_all");

  const [userId, setUserId] = useState<string>("");
  const [escopo, setEscopo] = useState<Escopo>("hoje");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [periodo, setPeriodo] = useState<Periodo>("dia");
  const [horaIni, setHoraIni] = useState("09:00");
  const [horaFim, setHoraFim] = useState("10:00");
  const [motivo, setMotivo] = useState("");
  // Recorrência semanal: dias da semana marcados e até quando repete.
  const [diasDaSemana, setDiasDaSemana] = useState<number[]>([]);
  const [ate12, setAte12] = useState("");

  const usersQuery = trpc.agenda.meetings.listUsers.useQuery(undefined, {
    enabled: open && podeGerenciarOutros,
  });
  const alvoId = userId ? Number(userId) : undefined;
  const availabilityQuery = trpc.agenda.availability.list.useQuery(
    { userId: alvoId },
    { enabled: open && alvoId != null },
  );

  // Preenche ao abrir: do bloqueio (edição) ou dos defaults (clique na grade).
  useEffect(() => {
    if (!open) return;
    if (bloqueio) {
      const a = new Date(bloqueio.startAt);
      const b = new Date(bloqueio.endAt);
      setUserId(String(bloqueio.userId));
      setEscopo("datas");
      setDe(isoLocalDate(a));
      // Dia inteiro termina à meia-noite do dia seguinte; o "até" mostrado é o
      // último dia coberto.
      const ultimo = bloqueio.allDay ? new Date(b.getTime() - 60_000) : b;
      setAte(isoLocalDate(ultimo));
      setPeriodo(bloqueio.allDay ? "dia" : "horario");
      setHoraIni(hhmmLocal(a));
      setHoraFim(hhmmLocal(b));
      setMotivo(bloqueio.reason ?? "");
      return;
    }
    const ini = defaults?.startAt ?? new Date();
    const fim = defaults?.endAt ?? new Date(ini.getTime() + 3600_000);
    setUserId(String(defaults?.userId ?? meuId ?? ""));
    // Veio de um clique num horário da grade: a data já está decidida.
    setEscopo(defaults?.startAt ? "datas" : "hoje");
    setDe(isoLocalDate(ini));
    setAte(isoLocalDate(fim));
    setPeriodo(defaults?.startAt ? "horario" : "dia");
    setHoraIni(hhmmLocal(ini));
    setHoraFim(hhmmLocal(fim));
    setMotivo("");
  }, [open, bloqueio, defaults, meuId]);

  /** Atalho de dias → (de, até). "Esta semana" é daqui até sexta. */
  function aplicarEscopo(e: Escopo) {
    setEscopo(e);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    if (e === "recorrente") {
      // Começa hoje, repete no dia da semana de hoje, por 8 semanas — um
      // ponto de partida que quase sempre é ajustado, mas nunca vazio.
      setDe(isoLocalDate(hoje));
      setDiasDaSemana((d) => (d.length ? d : [hoje.getDay()]));
      setAte12((v) => v || isoLocalDate(somaDias(hoje, 56)));
      if (periodo === "dia") setPeriodo("tarde");
      return;
    }
    if (e === "hoje") {
      setDe(isoLocalDate(hoje));
      setAte(isoLocalDate(hoje));
    } else if (e === "amanha") {
      const d = somaDias(hoje, 1);
      setDe(isoLocalDate(d));
      setAte(isoLocalDate(d));
    } else if (e === "semana") {
      // Até a sexta desta semana; no fim de semana, até a sexta seguinte.
      const dow = hoje.getDay();
      const ateSexta = somaDias(hoje, dow === 0 ? 5 : dow === 6 ? 6 : 5 - dow);
      setDe(isoLocalDate(hoje));
      setAte(isoLocalDate(ateSexta));
    }
  }

  // Manhã/Tarde da pessoa no dia escolhido: primeira e última faixa da semana
  // típica. Sem cadastro, um palpite comercial razoável.
  const faixasDoDia = useMemo(() => {
    if (!de) return [];
    const dow = new Date(`${de}T12:00:00`).getDay();
    return (availabilityQuery.data ?? [])
      .filter((f: any) => f.dayOfWeek === dow)
      .map((f: any) => ({ ini: f.startTime as string, fim: f.endTime as string }))
      .sort((a, b) => hhmmParaMinutos(a.ini) - hhmmParaMinutos(b.ini));
  }, [availabilityQuery.data, de]);
  const manha = faixasDoDia[0] ?? { ini: "08:00", fim: "12:00" };
  const tarde = faixasDoDia[faixasDoDia.length - 1] ?? { ini: "13:00", fim: "18:00" };
  const usaTardeIgualManha = faixasDoDia.length === 1;

  const aoSalvar = () => {
    toast.success(bloqueio ? "Bloqueio atualizado" : "Agenda bloqueada");
    utils.agenda.blocks.list.invalidate();
    onClose();
  };
  const criar = trpc.agenda.blocks.create.useMutation({
    onSuccess: (r: any, vars) => {
      if (vars.semanal) {
        toast.success(`Repetição criada — ${r?.criados ?? 0} bloqueios`);
        utils.agenda.blocks.list.invalidate();
        onClose();
        return;
      }
      if (vars.todaEquipe) {
        toast.success("Agenda de toda a equipe bloqueada");
        utils.agenda.blocks.list.invalidate();
        onClose();
        return;
      }
      aoSalvar();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao bloquear")),
  });
  const atualizar = trpc.agenda.blocks.update.useMutation({
    onSuccess: aoSalvar,
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar o bloqueio")),
  });
  const remover = trpc.agenda.blocks.delete.useMutation({
    onSuccess: () => {
      toast.success("Bloqueio removido");
      utils.agenda.blocks.list.invalidate();
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao remover o bloqueio")),
  });
  const ocupado = criar.isPending || atualizar.isPending || remover.isPending;

  function intervalo(): { startAt: Date; endAt: Date; allDay: boolean } | null {
    if (!de) return null;
    const fimDia = ate && ate >= de ? ate : de;
    if (periodo === "dia") {
      const startAt = new Date(`${de}T00:00:00`);
      const endAt = new Date(`${fimDia}T00:00:00`);
      endAt.setDate(endAt.getDate() + 1);
      return { startAt, endAt, allDay: true };
    }
    const [hi, hf] =
      periodo === "manha"
        ? [manha.ini, manha.fim]
        : periodo === "tarde"
          ? [tarde.ini, tarde.fim]
          : [horaIni, horaFim];
    const startAt = new Date(`${de}T${hi}:00`);
    const endAt = new Date(`${fimDia}T${hf}:00`);
    return { startAt, endAt, allDay: false };
  }

  // "Só esta agenda ou toda a equipe?" — perguntado na hora de criar, para
  // quem pode gerenciar a disponibilidade dos outros e tem mais de um colega.
  const [perguntandoEquipe, setPerguntandoEquipe] = useState(false);
  const temEquipe = podeGerenciarOutros && (usersQuery.data?.length ?? 0) > 1;

  const ehRecorrente = escopo === "recorrente" && !bloqueio;

  /** As horas que a recorrência usa — as mesmas do período escolhido. */
  function horasDoPeriodo(): { ini: string; fim: string } {
    if (periodo === "dia") return { ini: "00:00", fim: "23:59" };
    if (periodo === "manha") return manha;
    if (periodo === "tarde") return tarde;
    return { ini: horaIni, fim: horaFim };
  }

  function validarRecorrencia(): string | null {
    if (!de || !ate12) return "Escolha o período da repetição.";
    if (ate12 < de) return "O fim da repetição precisa ser depois do início.";
    if (diasDaSemana.length === 0) return "Marque pelo menos um dia da semana.";
    const h = horasDoPeriodo();
    if (periodo !== "dia" && h.fim <= h.ini) return "O fim precisa ser depois do início.";
    // Um ano é o teto: além disso a pessoa quer mexer na semana típica, não
    // criar centenas de bloqueios.
    const umAno = new Date(`${de}T12:00:00`);
    umAno.setFullYear(umAno.getFullYear() + 1);
    if (new Date(`${ate12}T12:00:00`) > umAno) {
      return "A repetição vale por até um ano. Para algo permanente, ajuste os horários de reunião nas configurações.";
    }
    return null;
  }

  function salvar() {
    const reason = motivo.trim() || null;
    if (bloqueio) {
      const iv = intervalo();
      if (!iv) return toast.error("Escolha o dia.");
      if (iv.endAt <= iv.startAt) return toast.error("O fim precisa ser depois do início.");
      atualizar.mutate({ id: bloqueio.id, ...iv, reason });
      return;
    }
    if (ehRecorrente) {
      const erro = validarRecorrencia();
      if (erro) return toast.error(erro);
    } else {
      const iv = intervalo();
      if (!iv) return toast.error("Escolha o dia.");
      if (iv.endAt <= iv.startAt) return toast.error("O fim precisa ser depois do início.");
    }
    if (temEquipe) {
      setPerguntandoEquipe(true);
      return;
    }
    criarPara(false);
  }

  function criarPara(todaEquipe: boolean) {
    setPerguntandoEquipe(false);
    const reason = motivo.trim() || null;
    if (ehRecorrente) {
      const h = horasDoPeriodo();
      criar.mutate({
        userId: alvoId,
        todaEquipe,
        reason,
        allDay: periodo === "dia",
        // O servidor calcula as ocorrências; `startAt`/`endAt` vão só para
        // satisfazer o contrato do input (a primeira ocorrência).
        startAt: new Date(`${de}T${h.ini}:00`),
        endAt: new Date(`${de}T${periodo === "dia" ? "23:59" : h.fim}:00`),
        semanal: { de, ate: ate12, diasDaSemana, horaIni: h.ini, horaFim: h.fim },
      });
      return;
    }
    const iv = intervalo();
    if (!iv) return;
    criar.mutate({ userId: alvoId, todaEquipe, ...iv, reason });
  }

  const nomeDoAlvo =
    alvoId === meuId
      ? "Você"
      : (usersQuery.data ?? []).find((u: any) => u.id === alvoId)?.name?.split(" ")[0] ?? "Esta pessoa";
  const nomeAlvo = alvoId === meuId ? "só a minha agenda" : `só a agenda de ${nomeDoAlvo}`;

  const periodos: Array<{ v: Periodo; label: string; sub: string }> = [
    { v: "dia", label: "Dia inteiro", sub: "00:00 – 23:59" },
    { v: "manha", label: "Manhã", sub: `${manha.ini} – ${manha.fim}` },
    ...(usaTardeIgualManha
      ? []
      : [{ v: "tarde" as Periodo, label: "Tarde", sub: `${tarde.ini} – ${tarde.fim}` }]),
    { v: "horario", label: "Horário", sub: "você escolhe" },
  ];

  // A frase que a pessoa confirmaria em voz alta — é ela que pega o "bloqueei
  // a semana inteira sem querer".
  const resumo = useMemo(() => {
    if (!de) return "";
    if (escopo === "recorrente" && !bloqueio) {
      if (diasDaSemana.length === 0 || !ate12) return "";
      const nomes = [...diasDaSemana].sort().map((d) => DIAS_LONGOS[d].replace("-feira", "")).join(", ");
      const h = periodo === "dia" ? "o dia inteiro" : periodo === "manha" ? `de manhã (${manha.ini} – ${manha.fim})` : periodo === "tarde" ? `à tarde (${tarde.ini} – ${tarde.fim})` : `das ${horaIni} às ${horaFim}`;
      const quem = nomeDoAlvo === "Você" ? "Você fica" : `${nomeDoAlvo} fica`;
      return `${quem} indisponível toda ${nomes}, ${h}, de ${formatarDia(de)} até ${formatarDia(ate12)}${motivo.trim() ? ` — ${motivo.trim()}` : ""}.`;
    }
    const dias =
      !ate || ate === de ? formatarDiaLongo(de) : `de ${formatarDia(de)} a ${formatarDia(ate)}`;
    const parte =
      periodo === "dia"
        ? "o dia inteiro"
        : periodo === "manha"
          ? `de manhã (${manha.ini} – ${manha.fim})`
          : periodo === "tarde"
            ? `à tarde (${tarde.ini} – ${tarde.fim})`
            : `das ${horaIni} às ${horaFim}`;
    const quem = nomeDoAlvo === "Você" ? "Você fica" : `${nomeDoAlvo} fica`;
    return `${quem} indisponível ${dias}, ${parte}${motivo.trim() ? ` — ${motivo.trim()}` : ""}. Reuniões já marcadas nesse período continuam na agenda.`;
  }, [de, ate, ate12, escopo, bloqueio, diasDaSemana, periodo, horaIni, horaFim, motivo, nomeDoAlvo, manha.ini, manha.fim, tarde.ini, tarde.fim]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-white dark:bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarOff className="h-4 w-4" />
            {bloqueio ? "Editar bloqueio" : "Bloquear agenda"}
          </DialogTitle>
          <DialogDescription>
            No período bloqueado a pessoa sai da lista de disponíveis e ninguém marca reunião ali.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {podeGerenciarOutros && !bloqueio && (
            <div>
              <Label className="text-xs">Agenda de</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Escolha a pessoa" />
                </SelectTrigger>
                <SelectContent>
                  {(usersQuery.data ?? []).map((u: any) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name ?? u.email}
                      {u.id === meuId ? " (você)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── Quais dias ───────────────────────────────────────────── */}
          <div>
            <Label className="text-xs">Quais dias</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {([
                { v: "hoje", label: "Hoje" },
                { v: "amanha", label: "Amanhã" },
                { v: "semana", label: "Esta semana" },
                { v: "datas", label: "Escolher datas" },
                ...(bloqueio ? [] : [{ v: "recorrente" as Escopo, label: "Toda semana" }]),
              ] as Array<{ v: Escopo; label: string }>).map((e) => (
                <button
                  key={e.v}
                  type="button"
                  onClick={() => aplicarEscopo(e.v)}
                  className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                    escopo === e.v
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>
            {escopo === "recorrente" ? (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() =>
                        setDiasDaSemana((prev) =>
                          prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
                        )
                      }
                      className={`h-8 w-10 rounded-md border text-xs transition-colors ${
                        diasDaSemana.includes(d)
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      }`}
                    >
                      {DIAS_CURTOS[d]}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">de</span>
                  <Input
                    type="date"
                    className="h-9 w-40"
                    value={de}
                    onChange={(e) => setDe(e.target.value)}
                  />
                  <span className="text-xs text-muted-foreground">até</span>
                  <Input
                    type="date"
                    className="h-9 w-40"
                    min={de || undefined}
                    value={ate12}
                    onChange={(e) => setAte12(e.target.value)}
                  />
                </div>
              </div>
            ) : escopo === "datas" ? (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Input
                  type="date"
                  className="h-9 w-40"
                  value={de}
                  onChange={(e) => {
                    setDe(e.target.value);
                    if (!ate || ate < e.target.value) setAte(e.target.value);
                  }}
                />
                <span className="text-sm text-muted-foreground">até</span>
                <Input
                  type="date"
                  className="h-9 w-40"
                  min={de || undefined}
                  value={ate}
                  onChange={(e) => setAte(e.target.value)}
                />
              </div>
            ) : (
              <p className="mt-1.5 text-[11px] text-muted-foreground first-letter:uppercase">
                {!ate || ate === de ? formatarDiaLongo(de) : `${formatarDia(de)} – ${formatarDia(ate)}`}
              </p>
            )}
          </div>

          {/* ── Qual parte do dia ────────────────────────────────────── */}
          <div>
            <Label className="text-xs">Qual parte do dia</Label>
            <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {periodos.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPeriodo(p.v)}
                  className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${
                    periodo === p.v ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  }`}
                >
                  <span className={`block text-xs font-medium ${periodo === p.v ? "text-primary" : ""}`}>
                    {p.label}
                  </span>
                  <span className="block text-[10px] text-muted-foreground mt-0.5">{p.sub}</span>
                </button>
              ))}
            </div>
            {periodo === "horario" && (
              <div className="mt-2 flex items-center gap-2">
                <Input type="time" className="h-9 w-28" value={horaIni} onChange={(e) => setHoraIni(e.target.value)} />
                <span className="text-sm text-muted-foreground">até</span>
                <Input type="time" className="h-9 w-28" value={horaFim} onChange={(e) => setHoraFim(e.target.value)} />
              </div>
            )}
            {(periodo === "manha" || periodo === "tarde") && faixasDoDia.length > 0 && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">Pela semana típica desta pessoa.</p>
            )}
          </div>

          {/* ── Motivo ───────────────────────────────────────────────── */}
          <div>
            <Label className="text-xs">
              Motivo <span className="text-muted-foreground">· aparece para a equipe</span>
            </Label>
            <Input
              className="mt-1 h-9"
              placeholder="Opcional"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") salvar(); }}
            />
            <div className="mt-1.5 flex flex-wrap gap-1">
              {MOTIVOS_RAPIDOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotivo(m)}
                  className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors ${
                    motivo === m
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {resumo && (
            <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
              {resumo}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {bloqueio ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={ocupado}
              onClick={() => remover.mutate({ id: bloqueio.id })}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Remover
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={ocupado}>
              Cancelar
            </Button>
            <Button size="sm" onClick={salvar} disabled={ocupado || !de || (ehRecorrente && diasDaSemana.length === 0)}>
              {ocupado ? "Salvando…" : bloqueio ? "Salvar" : "Bloquear"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={perguntandoEquipe} onOpenChange={(o) => !o && setPerguntandoEquipe(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bloquear para quem?</AlertDialogTitle>
            <AlertDialogDescription>
              Feriado ou evento da empresa vale para todo mundo; férias e médico, só para a pessoa.
              Bloquear toda a equipe cria um bloqueio igual na agenda de cada colega ativo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:justify-between">
            <AlertDialogCancel className="sm:mr-auto">Cancelar</AlertDialogCancel>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => criarPara(false)}>
                {primeiraMaiuscula(nomeAlvo)}
              </Button>
              <Button size="sm" onClick={() => criarPara(true)}>
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Toda a equipe
              </Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function primeiraMaiuscula(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function hhmmLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

function formatarDia(iso: string): string {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function formatarDiaLongo(iso: string): string {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}
